/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/12/22, 10:39 AM
 *
 */
/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/1/22, 3:54 PM
 *
 */

import React, { useEffect, useState } from 'react';
import LetteredAvatar from 'react-avatar';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { useAppSelector } from 'stores';

import { useInvoiceHistory } from 'features/members/api/query/useInvoiceHistory';
import { useMemberSubsDetails } from 'features/members/api/query/useMemberSubscription';
import { Invoice } from 'features/members/types';

import { Headings } from '@sunyahealthnepal/react-ui-components';
import Loader from 'components/Loader';

export const InvoicePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useAppSelector((state) => state.auth.user);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const invoiceId = Number(searchParams.get('id'));

  const { data: selectedSubscription } = useMemberSubsDetails(user?.id ?? 0);
  const [paid, setPaid] = useState(false);

  const { data: invoiceList, isFetching, isLoading } = useInvoiceHistory(user?.id ?? 0, invoiceId);
  const selectedSubsInvoice = invoiceList?.find((invoice) => invoice.invoice_no === invoiceId);

  const [invoiceData, setInvoiceData] = useState({
    gross_amount: 0,
    vat_amount: 0,
    discount_amount: 0,
    net_amount: 0,
  });

  const getInvoiceData = (discount_per: number, vat_per: number) => {
    if (!selectedSubscription) return;
    const price = Number(
      selectedSubscription.plan
        ? selectedSubscription.plan.price
        : selectedSubsInvoice?.subscription_detail.plan.price
    );
    return {
      gross_amount: price,
      discount_amount: price * (discount_per / 100),
      vat_amount: price * (vat_per / 100),
      net_amount: price - price * (discount_per / 100) + price * (vat_per / 100),
    };
  };

  useEffect(() => {
    if (invoiceList && invoiceId) {
      const selectedInvoice = invoiceList.find((invoice) => invoice.invoice_no === invoiceId);

      if (selectedInvoice) {
        setSelectedInvoice(selectedInvoice);
        setInvoiceData({
          gross_amount: selectedInvoice.gross_amount,
          discount_amount: selectedInvoice.discount_amount,
          vat_amount: selectedInvoice.vat_amount,
          net_amount: selectedInvoice.net_amount,
        });
        setPaid(!!selectedInvoice.paid);
      } else {
        !isFetching && navigate('/404');
      }
    } else {
      const data = getInvoiceData(10, 13);
      data && setInvoiceData(data);
    }
  }, [selectedSubscription, invoiceId, invoiceList, isFetching]);

  return (
    <>
      <Helmet>
        <title>Invoice | School Health</title>
      </Helmet>
      {!selectedSubscription || isLoading ? (
        <Loader />
      ) : (
        <div className="overflow-visible px-10 py-10 sm:p-6">
          <div className="flex flex-col gap-y-8">
            <Headings title={`Invoice INV-0${invoiceId}`} subtitle={'Details of Invoice'} />

            <div className="flex w-full items-start gap-8 md:flex-col">
              <div className="flex w-3/4 flex-col gap-10 rounded-lg bg-white p-8 shadow-md md:w-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center ">
                    <div className="relative h-20 w-20 cursor-pointer">
                      <LetteredAvatar name="Sunya Health" size="60" round={true} maxInitials={2} />
                    </div>
                    <div className="ml-4 flex flex-col">
                      <div className="cursor-pointer text-[1.3rem] font-semibold capitalize text-slate-800">
                        Sunya Health
                      </div>
                      <a
                        href="mailto: contact@sunya.health"
                        className="text-lg font-medium text-gray-500 hover:text-gray-800"
                      >
                        contact@sunya.health
                      </a>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 text-right text-lg font-medium text-gray-500">
                    <span>Kathmandu - 44700</span>
                    <span>Bagmati Province</span>
                    <span> Nepal</span>
                  </div>
                </div>
                <div className="invoice-top-bg flex w-full items-center justify-between rounded-xl p-10 text-lg text-white shadow-lg">
                  <div className="flex flex-col gap-3">
                    <span className="font-medium">Invoice Number</span>
                    <span className="font-medium">INV-{invoiceId}</span>
                    <div className="flex gap-1">
                      <span className="text-gray-100">Issued Date:</span>
                      <span className="font-medium">{moment().format('DD MMM YYYY')}</span>
                    </div>
                    <div className="flex gap-1">
                      <span className="text-gray-100">Due Date:</span>
                      <span className="font-medium">
                        {moment().add(15, 'days').format('DD MMM YYYY')}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 text-right">
                    <span className="font-medium">Billed to</span>
                    <span>{user?.name}</span>
                    <span>{user?.address}</span>
                    <span>{user?.phone}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-xl font-medium text-gray-800">Subscription Details</span>
                    <span className="font-medium text-gray-400">Check below for subscriptions</span>
                  </div>
                </div>
                <div className="-mt-6 flex flex-col">
                  <div className="flex w-full gap-8 border-t-[1px] border-b-[1px] border-gray-400/40 p-4 text-lg text-gray-500/70">
                    <span className="w-2/3 font-medium">Subscription Name</span>

                    <span className="w-1/5 font-medium">Total Tests</span>
                    <span className="w-1/5 font-medium ">Period</span>
                    <span className="w-1/3 text-right font-medium">Subscription Price</span>
                  </div>
                  <div className="flex w-full items-center gap-8 border-b-[1px] border-gray-400/40 px-4 py-6 text-lg">
                    <div className="flex w-2/3 flex-col gap-2 font-medium text-gray-800">
                      <span className="text-xl">
                        {selectedSubscription.plan
                          ? selectedSubscription.plan.name
                          : selectedSubsInvoice?.subscription_detail.plan.name}
                      </span>
                      <span className="font-medium text-gray-400 md:text-sm">
                        Acuity, Vitals, ECG, BMI, Urine, Glucose
                      </span>
                    </div>

                    <span className="w-1/5 font-medium text-gray-700 ">5 tests</span>
                    <span className="w-1/5 font-medium text-gray-700 ">365 days</span>
                    <span className="w-1/3 text-right font-medium text-gray-700">
                      Rs.{' '}
                      {selectedSubscription?.plan
                        ? selectedSubscription.plan.price
                        : selectedSubsInvoice?.subscription_detail.plan.price}
                    </span>
                  </div>
                  <div className="flex w-full gap-8 px-4 py-12 text-lg md:relative md:flex-col-reverse">
                    <div className="flex w-2/3 flex-col gap-4 md:w-full">
                      <div className="item-center flex w-full justify-between">
                        <span className="text-xl font-medium text-gray-800">
                          Payment Information
                        </span>
                        <button
                          disabled
                          className="font-medium text-primary-600 disabled:cursor-not-allowed"
                        >
                          Select Payment
                        </button>
                      </div>

                      <div className="w-full rounded-xl bg-gray-50 p-6 shadow-sm">
                        <div className="flex flex-col">
                          <span className="pb-3 font-medium text-gray-800">Cash Payment</span>
                          <div className="pb-2 font-medium text-gray-500">
                            Name: <span className="font-medium text-gray-600">{user?.name} </span>
                          </div>
                          <div className="pb-2 font-medium text-gray-500">
                            Phone: <span className="font-medium text-gray-600">{user?.phone} </span>
                          </div>
                          <div className="font-medium text-gray-500">
                            Payment Received:{' '}
                            <span className="font-medium text-gray-600">
                              Rs. {invoiceData.net_amount}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="w-1/5 md:w-0" />

                    <div className="flex w-1/5 flex-col gap-4 font-medium text-gray-800 md:absolute md:top-12 md:w-full">
                      <span className="font-medium text-gray-800 ">Sub Total</span>
                      <div className="flex flex-col gap-4">
                        <span className="whitespace-nowrap font-medium text-gray-400">
                          Discount Amt.
                        </span>
                        <span className="font-medium text-gray-400">Total Tax</span>
                      </div>
                      <span className="mt-10 border-t-[1px] border-gray-400/40 pt-6 font-medium text-gray-400 md:border-0">
                        Total Amount
                      </span>
                    </div>

                    <div className="flex w-1/3 flex-col gap-4 text-right font-medium text-gray-700 md:w-full  ">
                      <span> Rs. {invoiceData.gross_amount}</span>
                      <div className="flex flex-col gap-4">
                        <span className="font-medium text-gray-700">
                          Rs. {invoiceData.discount_amount}
                        </span>
                        <span className="font-medium text-gray-700">
                          Rs. {invoiceData.vat_amount}
                        </span>
                      </div>
                      <span className="mt-10 -ml-8 border-t-[1px] border-gray-400/40 pt-4 text-2xl font-medium text-gray-800">
                        Rs. <span className="font-Inter">{invoiceData.net_amount}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex w-1/4 flex-col gap-8 md:w-full">
                <div className=" flex flex-col gap-4 rounded-lg bg-white p-6 shadow-md">
                  <span className="text-xl font-medium text-gray-800">Customer Details</span>
                  <div className="flex items-center ">
                    <div className="relative h-20 w-20 cursor-pointer">
                      {user?.image ? (
                        <img
                          src={user?.image}
                          alt="image"
                          className="relative h-20 w-20 overflow-hidden rounded-full object-contain"
                        />
                      ) : (
                        <LetteredAvatar name={user?.name} size="50" round={true} maxInitials={2} />
                      )}
                    </div>
                    <div className="ml-4 flex flex-col gap-1">
                      <div className="cursor-pointer text-[1.3rem] font-bold  capitalize text-slate-800">
                        {user?.name}
                      </div>
                      <a
                        href={`mailto: ${user?.email}`}
                        className="text-lg font-medium text-gray-500 hover:text-gray-800"
                      >
                        {user?.email}
                      </a>
                    </div>
                  </div>
                  <hr className="border-t-[1px] border-gray-400/40" />
                  <div className="flex flex-col">
                    <span className="text-xl font-medium text-gray-800">Phone: {user?.phone}</span>
                    <span className="text-xl font-medium text-gray-400">{user?.address}</span>
                  </div>
                </div>

                {selectedInvoice ? (
                  <div className=" flex flex-col gap-4 rounded-lg bg-white p-6 shadow-md">
                    <div className="flex gap-1 text-lg capitalize">
                      <span className="font-medium text-gray-900">
                        Amount {paid ? 'Paid' : 'Due'}{' '}
                      </span>
                      <span className="font-medium text-gray-400">(NPR)</span>
                    </div>
                    <div className="flex items-end gap-1 text-xl capitalize">
                      <span className="font-Inter text-3xl font-medium leading-7 text-slate-900">
                        Rs. {invoiceData.net_amount}
                      </span>
                      <span className="text-base font-medium text-gray-400">(Tax Incl.)</span>
                    </div>
                    <div
                      className={`rounded-xl py-3 px-6 ${
                        paid ? 'bg-primary-100 text-primary-600' : 'bg-error-100 text-error-500'
                      }  self-start text-lg font-medium shadow-sm`}
                    >
                      {paid ? 'Paid' : 'Due'} on{' '}
                      {selectedInvoice && paid
                        ? moment.unix(selectedInvoice.transaction_date / 1000).format('DD MMM YYYY')
                        : moment().add(15, 'days').format('DD MMM YYYY')}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
